// @refresh reset
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '@/hooks/useAuth';
import { deviceService } from '@/api/services';
import { POLLING_INTERVAL } from '@/config/constants';
import type {
  AccountDeviceRespObj,
  DeviceInfoRespObj,
} from '@/api/models';

export interface DeviceContextValue {
  devices: AccountDeviceRespObj[];
  devicesLoading: boolean;
  devicesError: string | null;
  selectedDevice: AccountDeviceRespObj | null;
  selectedDeviceInfo: DeviceInfoRespObj | null;
  deviceInfoLoading: boolean;
  deviceInfoError: string | null;
  selectDevice: (deviceUUID: string) => void;
  clearSelectedDevice: () => void;
  refreshDevices: () => Promise<void>;
  refreshDeviceInfo: () => Promise<void>;
}

export const DeviceContext = createContext<DeviceContextValue | null>(null);

export function DeviceProvider({ children }: { children: ReactNode }) {
  const { accountUUID, isAuthenticated } = useAuth();

  // Device list state
  const [devices, setDevices] = useState<AccountDeviceRespObj[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesError, setDevicesError] = useState<string | null>(null);

  // Selected device state
  const [selectedDevice, setSelectedDevice] = useState<AccountDeviceRespObj | null>(null);
  const [selectedDeviceInfo, setSelectedDeviceInfo] = useState<DeviceInfoRespObj | null>(null);
  const [deviceInfoLoading, setDeviceInfoLoading] = useState(false);
  const [deviceInfoError, setDeviceInfoError] = useState<string | null>(null);

  // Polling timer ref
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch device list
  const refreshDevices = useCallback(async () => {
    if (!accountUUID) return;

    setDevicesLoading(true);
    setDevicesError(null);
    try {
      const resp = await deviceService.getAccountDevices(accountUUID);
      setDevices(resp.deviceList || []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load devices';
      setDevicesError(message);
    } finally {
      setDevicesLoading(false);
    }
  }, [accountUUID]);

  // Fetch full device info (with sensor data)
  const refreshDeviceInfo = useCallback(async () => {
    if (!selectedDevice) return;

    const deviceUUID = selectedDevice.deviceProperties.deviceUUID;
    // Only show loading spinner on initial fetch, not on poll refreshes
    if (!selectedDeviceInfo) {
      setDeviceInfoLoading(true);
    }
    setDeviceInfoError(null);

    try {
      const info = await deviceService.getDeviceInfo({
        deviceUUID,
        includeSensorData: true,
        includeEventList: true,
        includeMTList: true,
        includeMOList: false,
        includeSMSList: false,
        includeEmailList: false,
        includeLocationList: false,
        includePushNotificationList: false,
        includeVoiceCallList: false,
        eventLogReq: {
          deviceUUID,
          maxNumDays: 7,
          maxNumEntries: 20,
        },
      });
      setSelectedDeviceInfo(info);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load device info';
      setDeviceInfoError(message);
    } finally {
      setDeviceInfoLoading(false);
    }
  }, [selectedDevice, selectedDeviceInfo]);

  // Select a device by UUID
  const selectDevice = useCallback(
    (deviceUUID: string) => {
      const device = devices.find(
        (d) => d.deviceProperties.deviceUUID === deviceUUID,
      );
      if (device) {
        setSelectedDevice(device);
        setSelectedDeviceInfo(null);
        setDeviceInfoError(null);
      }
    },
    [devices],
  );

  const clearSelectedDevice = useCallback(() => {
    setSelectedDevice(null);
    setSelectedDeviceInfo(null);
    setDeviceInfoError(null);
  }, []);

  // Load device list on auth
  useEffect(() => {
    if (isAuthenticated && accountUUID) {
      refreshDevices();
    } else {
      setDevices([]);
      setSelectedDevice(null);
      setSelectedDeviceInfo(null);
    }
  }, [isAuthenticated, accountUUID, refreshDevices]);

  // Fetch device info when selection changes
  useEffect(() => {
    if (selectedDevice) {
      // Fetch immediately
      const deviceUUID = selectedDevice.deviceProperties.deviceUUID;
      setDeviceInfoLoading(true);
      setDeviceInfoError(null);
      setSelectedDeviceInfo(null);

      deviceService
        .getDeviceInfo({
          deviceUUID,
          includeSensorData: true,
          includeEventList: true,
          includeMTList: true,
          includeMOList: false,
          includeSMSList: false,
          includeEmailList: false,
          includeLocationList: false,
          includePushNotificationList: false,
          includeVoiceCallList: false,
          eventLogReq: {
            deviceUUID,
            maxNumDays: 7,
            maxNumEntries: 20,
          },
        })
        .then((info) => {
          setSelectedDeviceInfo(info);
          setDeviceInfoLoading(false);
        })
        .catch((err) => {
          setDeviceInfoError(
            err instanceof Error ? err.message : 'Failed to load device info',
          );
          setDeviceInfoLoading(false);
        });
    }
  }, [selectedDevice]);

  // 5-second polling for selected device info
  useEffect(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    if (!selectedDevice) return;

    const deviceUUID = selectedDevice.deviceProperties.deviceUUID;

    pollingRef.current = setInterval(async () => {
      try {
        const info = await deviceService.getDeviceInfo({
          deviceUUID,
          includeSensorData: true,
          includeEventList: true,
          includeMTList: true,
          includeMOList: false,
          includeSMSList: false,
          includeEmailList: false,
          includeLocationList: false,
          includePushNotificationList: false,
          includeVoiceCallList: false,
          eventLogReq: {
            deviceUUID,
            maxNumDays: 7,
            maxNumEntries: 20,
          },
        });
        setSelectedDeviceInfo(info);
      } catch {
        // Silently fail on poll — don't overwrite existing data with error
      }
    }, POLLING_INTERVAL);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [selectedDevice]);

  const value = useMemo<DeviceContextValue>(
    () => ({
      devices,
      devicesLoading,
      devicesError,
      selectedDevice,
      selectedDeviceInfo,
      deviceInfoLoading,
      deviceInfoError,
      selectDevice,
      clearSelectedDevice,
      refreshDevices,
      refreshDeviceInfo,
    }),
    [
      devices,
      devicesLoading,
      devicesError,
      selectedDevice,
      selectedDeviceInfo,
      deviceInfoLoading,
      deviceInfoError,
      selectDevice,
      clearSelectedDevice,
      refreshDevices,
      refreshDeviceInfo,
    ],
  );

  return (
    <DeviceContext.Provider value={value}>{children}</DeviceContext.Provider>
  );
}
